import type { usedBySummary } from "@/lib/attributeReview/usedBySummary";

import { muted } from "./agentStyles";

export type UsedByProps = {
  summary: ReturnType<typeof usedBySummary>;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  fieldLabel: string;
  pickLabel: string;
};

const SHOWN = 3;
const skeleton =
  "h-2.5 rounded-[5px] bg-slate-200 motion-safe:animate-pulse dark:bg-slate-800";

export function UsedByBody({
  summary,
  loading,
  failed,
  onRetry,
  fieldLabel,
  pickLabel,
}: UsedByProps) {
  if (loading)
    return (
      <div aria-hidden className="flex flex-col gap-2">
        <div className={`${skeleton} w-[62%]`} />
        <div className="flex justify-between gap-2.5">
          <div className={`${skeleton} w-[55%]`} />
          <div className={`${skeleton} w-[30%]`} />
        </div>
        <div className="flex justify-between gap-2.5">
          <div className={`${skeleton} w-[48%]`} />
          <div className={`${skeleton} w-[36%]`} />
        </div>
      </div>
    );
  if (failed)
    return (
      <p className="flex items-center justify-between gap-2 text-sm text-rose-700 dark:text-rose-300">
        Could not check other stores.
        <button
          type="button"
          onClick={onRetry}
          className="font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          Retry
        </button>
      </p>
    );
  if (summary.count === 0)
    return (
      <div className="space-y-0.5 text-sm">
        <p className="font-medium">{summary.title}</p>
        <p className={muted}>
          Saving it makes {pickLabel} available. The next store that asks the
          same fact can link to it.
        </p>
      </div>
    );
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
        {summary.title}
      </p>
      {summary.rows.slice(0, SHOWN).map((r) => (
        <p key={r.where} className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 flex-1 truncate text-[13px]">{r.where}</span>
          <span className={`max-w-[48%] text-right text-xs ${muted}`}>
            {r.as}
            {r.as.toLowerCase() === `as “${fieldLabel}”`.toLowerCase() && (
              <span className="ml-1.5 rounded bg-slate-100 px-1 text-[11px] dark:bg-slate-800">
                same name
              </span>
            )}
          </span>
        </p>
      ))}
    </div>
  );
}
